import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { 
  AlertTriangle, 
  Clock, 
  Phone, 
  Calendar, 
  FileText, 
  PlayCircle,
  PauseCircle,
  Volume2,
  ChevronRight,
  Thermometer,
  Heart,
  Activity,
  ArrowLeft,
  Video
} from 'lucide-react';
import { TriageData, TriageOutcome } from '@/types/triage';
import { format } from 'date-fns';
import { CollapsibleCard } from '@/components/ui/collapsible-card';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { GuidedNextSteps } from './GuidedNextSteps';
import { useAudioTriage } from '@/contexts/AudioTriageContext';
import { useProviders } from '@/hooks/useProvidersQuery';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface AudioTriageResultScreenProps {
  result?: TriageOutcome;
  triageData?: TriageData;
  triageId?: string;
  patientId?: string;
  audioUrl?: string; // URL to the audio recording
}

export function AudioTriageResultScreen({ 
  result, 
  triageData, 
  triageId, 
  patientId,
  audioUrl 
}: AudioTriageResultScreenProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [showProviderSelection, setShowProviderSelection] = useState(false);
  const { profile } = useAuth();
  const { data: providers, isLoading: loadingProviders } = useProviders(profile?.clinic_id || '');

  // Get real data from AudioTriageContext
  const { state } = useAudioTriage();
  const contextTriageData = state.triageData;
  const contextTriageResult = state.triageResult;
  const contextAudioUrl = state.audioUrl;

  // Use context data first, then props, then fallback to mock data only if no real data exists
  const displayData = contextTriageData || triageData || {
    symptoms: ['No symptoms recorded'],
    severity: 'unknown',
    duration: 'Unknown',
    onset: 'unknown',
    additionalInfo: '',
    vitals: {},
    medicalHistory: [],
    medications: [],
    allergies: []
  };

  const displayResult = contextTriageResult || result || {
    urgency: 'standard',
    manchesterCategory: 'Assessment pending',
    recommendation: 'Please complete your assessment.',
    redFlags: [],
    clinicalNotes: 'Assessment data not available.',
    nextSteps: ['Complete your triage assessment'],
    estimatedWaitTime: 'Unknown',
    presentingComplaint: 'Assessment incomplete',
    discriminator: 'No assessment data',
    aiConfidence: 0
  };

  const displayAudioUrl = contextAudioUrl || audioUrl;

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return 'bg-red-500 text-white';
      case 'urgent': return 'bg-orange-500 text-white';
      case 'standard': return 'bg-yellow-500 text-black';
      case 'non-urgent': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getUrgencyEmoji = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return '🔴';
      case 'urgent': return '🟠';
      case 'standard': return '🟡';
      case 'non-urgent': return '🟢';
      default: return '⚪';
    }
  };

  const getRecommendationIcon = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return <Phone className="w-6 h-6" />;
      case 'urgent': return <AlertTriangle className="w-6 h-6" />;
      case 'standard': return <Calendar className="w-6 h-6" />;
      case 'non-urgent': return <Clock className="w-6 h-6" />;
      default: return <Activity className="w-6 h-6" />;
    }
  };

  const handlePlayAudio = () => {
    if (!audioElement && displayAudioUrl) {
      const audio = new Audio(displayAudioUrl);
      setAudioElement(audio);
      audio.play();
      setIsPlaying(true);
      audio.onended = () => setIsPlaying(false);
    } else if (audioElement) {
      if (isPlaying) {
        audioElement.pause();
      } else {
        audioElement.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const downloadSummary = () => {
    const summary = `
AUDIO TRIAGE ASSESSMENT SUMMARY
Generated: ${format(new Date(), 'PPpp')}

URGENCY LEVEL: ${displayResult.urgency.toUpperCase()}
MANCHESTER TRIAGE CATEGORY: ${displayResult.manchesterCategory}

SYMPTOMS:
${displayData.symptoms.map(s => `• ${s}`).join('\n')}

SEVERITY: ${displayData.severity}
DURATION: ${displayData.duration}
ONSET: ${displayData.onset}

VITAL SIGNS:
${displayData.vitals.temperature ? `Temperature: ${typeof displayData.vitals.temperature === 'number' ? displayData.vitals.temperature.toFixed(1) : displayData.vitals.temperature}°C` : ''}
${displayData.vitals.heartRate ? `Heart Rate: ${displayData.vitals.heartRate} bpm` : ''}
${displayData.vitals.bloodPressure ? `Blood Pressure: ${displayData.vitals.bloodPressure}` : ''}

RECOMMENDATION:
${displayResult.recommendation}

RED FLAGS IDENTIFIED:
${displayResult.redFlags.length > 0 ? displayResult.redFlags.map(f => `• ${f}`).join('\n') : 'None identified'}

NEXT STEPS:
${displayResult.nextSteps.map(step => `• ${step}`).join('\n')}

CLINICAL NOTES:
${displayResult.clinicalNotes}

---
This assessment is for informational purposes only and does not replace professional medical advice.
    `;

    const blob = new Blob([summary], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audio-triage-summary-${format(new Date(), 'yyyy-MM-dd-HHmm')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleBackToHome = () => {
    navigate('/patient/dashboard'); // Navigate to the patient dashboard
  };

  const handleUrgentConnect = () => {
    navigate('/patient/audio-triage/urgent-connect');
  };

  const handleBookAppointment = () => {
    // Pass triage data and result as state to pre-fill provider recommendations
    navigate('/patient/audio-triage/provider-select', {
      state: {
        triageData: displayData,
        triageResult: displayResult,
        audioUrl: displayAudioUrl
      }
    });
  };

  const handleProviderBooking = () => {
    if (!selectedProvider) {
      return;
    }
    
    const provider = providers.find(p => p.id === selectedProvider);
    if (provider) {
      // Navigate to booking confirmation with selected provider
      navigate('/patient/audio-triage/book/confirm', {
        state: {
          triageData: displayData,
          triageResults: displayResult,
          selectedProvider: provider,
          audioUrl: displayAudioUrl,
          primarySymptoms: displayResult.presentingComplaint || displayData.symptoms.join(", ")
        }
      });
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-100 p-4 sm:p-6">
      <div className="w-full max-w-3xl mx-auto bg-white p-6 sm:p-8 rounded-xl shadow-xl">
        {/* Dashboard navigation */}
        <div className="flex justify-end mb-4">
          <Button 
            variant="ghost" 
            onClick={handleBackToHome}
            className="text-sm flex items-center text-slate-600 hover:text-slate-900 p-2 h-auto"
            aria-label="Return to dashboard"
          >
            <ArrowLeft size={16} className="mr-1" /> Back to Dashboard
          </Button>
        </div>

        {/* Urgency Banner */}
        <div className={`w-full rounded-xl shadow-lg mb-6 overflow-hidden ${getUrgencyColor(displayResult.urgency)}`}>
          <div className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                {getRecommendationIcon(displayResult.urgency)}
              </div>
              <div>
                <h1 className="text-2xl font-bold mb-1">
                  {getUrgencyEmoji(displayResult.urgency)} {displayResult.urgency.charAt(0).toUpperCase() + displayResult.urgency.slice(1)} Attention
                </h1>
                <p className="text-lg opacity-90">{displayResult.recommendation}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Assessment Summary Card */}
        <CollapsibleCard
          title="Assessment Summary"
          description="Based on your audio description and follow-up answers"
          defaultOpen={true}
          className="bg-white shadow-xl rounded-xl mb-6"
        >
          <CardContent className="p-6">
            {/* Audio Playback Section */}
            {(displayAudioUrl || true) && (
              <div className="mb-6 bg-indigo-50 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center">
                  <Volume2 className="w-5 h-5 text-indigo-600 mr-3" />
                  <span className="font-medium text-indigo-900">Your symptom description</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePlayAudio}
                  className="flex items-center gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                >
                  {isPlaying ? (
                    <>
                      <PauseCircle size={16} /> Pause
                    </>
                  ) : (
                    <>
                      <PlayCircle size={16} /> Play
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Symptoms Section */}
            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-3 text-slate-800">Reported Symptoms</h3>
              <div className="flex flex-wrap gap-2">
                {displayData.symptoms.map((symptom, index) => (
                  <Badge key={index} variant="outline" className="bg-slate-100 text-slate-800 py-1 px-3 rounded-full">
                    {symptom}
                  </Badge>
                ))}
              </div>
            </div>

            <Separator className="my-4" />

            {/* Vitals Section */}
            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-3 text-slate-800">Vital Signs</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {displayData.vitals.temperature && (
                  <div className="flex items-center gap-3 bg-red-50 p-3 rounded-lg">
                    <Thermometer className="w-6 h-6 text-red-500" />
                    <div>
                      <p className="text-sm text-slate-600">Temperature</p>
                      <p className="font-medium text-slate-900">{typeof displayData.vitals.temperature === 'number' ? displayData.vitals.temperature.toFixed(1) : displayData.vitals.temperature}°C</p>
                    </div>
                  </div>
                )}
                {displayData.vitals.heartRate && (
                  <div className="flex items-center gap-3 bg-pink-50 p-3 rounded-lg">
                    <Heart className="w-6 h-6 text-pink-500" />
                    <div>
                      <p className="text-sm text-slate-600">Heart Rate</p>
                      <p className="font-medium text-slate-900">{displayData.vitals.heartRate} bpm</p>
                    </div>
                  </div>
                )}
                {displayData.vitals.bloodPressure && (
                  <div className="flex items-center gap-3 bg-blue-50 p-3 rounded-lg">
                    <Activity className="w-6 h-6 text-blue-500" />
                    <div>
                      <p className="text-sm text-slate-600">Blood Pressure</p>
                      <p className="font-medium text-slate-900">{displayData.vitals.bloodPressure}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Red Flags Section */}
            {displayResult.redFlags.length > 0 && (
              <>
                <Separator className="my-4" />
                <div className="mb-6">
                  <h3 className="font-semibold text-lg mb-3 text-slate-800">Important Findings</h3>
                  <div className="space-y-2">
                    {displayResult.redFlags.map((flag, index) => (
                      <div key={index} className="flex items-start gap-2 text-red-700">
                        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <p>{flag}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Next Steps Section */}
            <Separator className="my-4" />
            <div>
              <h3 className="font-semibold text-lg mb-3 text-slate-800">Recommended Next Steps</h3>
              <ul className="space-y-2">
                {displayResult.nextSteps.map((step, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <ChevronRight className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <p>{step}</p>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </CollapsibleCard>

        {/* Guided Next Steps */}
        <GuidedNextSteps 
          urgency={displayResult.urgency as 'immediate' | 'urgent' | 'standard' | 'non-urgent'}
          estimatedWaitTime={displayResult.estimatedWaitTime}
          nearestFacility={{
            name: "ClinicaFlow Urgent Care",
            distance: "1.2 miles",
            address: "123 Healthcare Ave, Medical District",
            phone: "(555) 123-4567"
          }}
          className="mb-8"
        />
        
        {/* Connect Button for Urgent/Immediate Cases */}
        {(displayResult.urgency === 'immediate' || displayResult.urgency === 'urgent') && (
          <Button
            onClick={handleUrgentConnect}
            className={`w-full mb-6 py-4 text-white flex items-center justify-center gap-2 ${displayResult.urgency === 'immediate' ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-600 hover:bg-orange-700'}`}
          >
            {displayResult.urgency === 'immediate' ? (
              <>
                <Phone className="w-5 h-5" />
                Connect to Emergency Services
              </>
            ) : (
              <>
                <Video className="w-5 h-5" />
                Connect to Tele-Triage
              </>
            )}
          </Button>
        )}
        
        {/* Action Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <Button 
            variant="outline" 
            onClick={downloadSummary}
            className="flex items-center justify-center gap-2 py-6 text-base"
          >
            <FileText className="w-5 h-5" />
            Download Summary
          </Button>
          
          {displayResult.urgency === 'immediate' ? (
            <Button 
              className="bg-red-600 hover:bg-red-700 flex items-center justify-center gap-2 py-6 text-base"
            >
              <Phone className="w-5 h-5" />
              Call Emergency Services
            </Button>
          ) : (
            <Button 
              onClick={() => setShowProviderSelection(!showProviderSelection)}
              className="bg-indigo-600 hover:bg-indigo-700 flex items-center justify-center gap-2 py-6 text-base"
            >
              <Calendar className="w-5 h-5" />
              {showProviderSelection ? 'Hide Provider Selection' : 'Book Appointment'}
            </Button>
          )}
        </div>

        {/* Provider Selection Section */}
        {showProviderSelection && displayResult.urgency !== 'immediate' && (
          <Card className="mb-8">
            <CardContent className="p-6">
              <h3 className="font-semibold text-lg mb-4 text-slate-800">Select Healthcare Provider</h3>
              <p className="text-sm text-slate-600 mb-4">
                Based on your triage assessment, we recommend these providers:
              </p>
              
              {loadingProviders ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  <span className="ml-3 text-slate-600">Loading providers...</span>
                </div>
              ) : !providers || providers.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-600 mb-4">No providers available at the moment.</p>
                  <p className="text-sm text-slate-500">Please try again later or contact support.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="provider">Choose Provider</Label>
                    <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a healthcare provider" />
                      </SelectTrigger>
                      <SelectContent>
                        {providers.map((provider) => (
                          <SelectItem key={provider.id} value={provider.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{provider.name}</span>
                              <span className="text-xs text-slate-500">{provider.specialty} • Available: {provider.nextAvailable}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedProvider && (
                    <div className="p-4 bg-slate-50 rounded-lg">
                      {(() => {
                        const provider = providers.find(p => p.id === selectedProvider);
                        return provider ? (
                          <div>
                            <p className="font-medium text-slate-800">{provider.name}</p>
                            <p className="text-sm text-slate-600">{provider.specialty}</p>
                            <p className="text-sm text-indigo-600">Available: {provider.nextAvailable}</p>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}

                  <Button 
                    onClick={handleProviderBooking}
                    disabled={!selectedProvider}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300"
                  >
                    Continue to Booking
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Disclaimer */}
        <p className="text-xs text-slate-600 text-center max-w-md mx-auto mb-8">
          This assessment is based on the information you provided and is not a substitute for professional medical advice, diagnosis, or treatment.
        </p>
      </div>
    </div>
  );
}
